import type { Optional, SetRequiredAndNonNullable } from '@festina-life/core'
import {
  FPlusCodeContainer,
  type FPlusViewerAlignment,
  IndicateOverflow,
} from '@festina-life/fplus'
import { Card, CardBody } from '@festina-life/ui/structure'
import cx from 'classnames'
import type { Ref } from 'react'

import { LiteralExpressionViewer } from './LiteralExpressionViewer'
import type { LiteralTypeInformation } from './types'

/**
 * The value wraps rather than running off the side, and is cut to the height
 * of the row it sits in.
 */
const clippingStyles = 'max-h-full overflow-hidden'

type Props = {
  typeInformation: SetRequiredAndNonNullable<LiteralTypeInformation, 'value'>
  alignment: FPlusViewerAlignment
  overflowTitle?: Optional<string>
}

/**
 * A literal value that shows itself when it fits its row, and gives way to the
 * overflow indicator when it does not, offering the value in full on hover.
 */
export const LiteralExpressionWithOverflowViewer = ({
  alignment,
  typeInformation,
  overflowTitle,
}: Props) => (
  <IndicateOverflow
    title={overflowTitle}
    className={alignment === 'center' ? 'text-center' : 'text-left'}
    tip={
      <Card>
        <CardBody>
          <FPlusCodeContainer
            className="min-w-250 text-left"
            isTransparent={false}
          >
            <LiteralExpressionViewer
              typeInformation={typeInformation}
              alignment={alignment}
            />
          </FPlusCodeContainer>
        </CardBody>
      </Card>
    }
  >
    {(ref) => (
      <LiteralExpressionViewer
        ref={ref as Ref<HTMLDivElement>}
        typeInformation={typeInformation}
        alignment={alignment}
        className={cx('w-full', clippingStyles)}
      />
    )}
  </IndicateOverflow>
)
